import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import Alert from 'antd/lib/alert';
import Input from 'antd/lib/input';
import Modal from 'antd/lib/modal';
import Text from 'antd/lib/typography/Text';
import Upload from 'antd/lib/upload';
import type { UploadFile } from 'antd/lib/upload/interface';
import { InboxOutlined } from '@ant-design/icons';

import { Task } from 'cvat-core-wrapper';
import { createTaskAsync } from 'actions/tasks-actions';
import { createReferenceAsync } from 'actions/references-actions';
import { t } from 'utils/i18n';
import {
    isDrawing, isPhoto, referencePayload, taskPayload, validate,
} from './new-task-form';

interface Props {
    projectId: number;
    onClose(): void;
    onCreated(task: Task): void;
}

interface ZoneProps {
    label: string;
    hint: string;
    files: UploadFile[];
    accepts(file: File): boolean;
    disabled: boolean;
    onChange(files: UploadFile[]): void;
}

export function DropZone({
    label, hint, files, accepts, disabled, onChange,
}: ZoneProps): JSX.Element {
    return (
        <div className='cvat-new-task-zone'>
            <Text strong>{label}</Text>
            <Upload.Dragger
                multiple
                disabled={disabled}
                fileList={files}
                // the browser must not upload: the files go with the task
                beforeUpload={(file) => (accepts(file) ? false : Upload.LIST_IGNORE)}
                onChange={({ fileList }) => onChange(fileList)}
                aria-label={label}
            >
                <p className='ant-upload-drag-icon'><InboxOutlined /></p>
                <p className='ant-upload-text'>{t('Drop files here or click to choose')}</p>
                <p className='ant-upload-hint'>{hint}</p>
            </Upload.Dragger>
        </div>
    );
}

export const originals = (files: UploadFile[]): File[] => (
    files.map((file) => file.originFileObj as unknown).filter((file): file is File => file instanceof File)
);

export default function NewTaskModal({ projectId, onClose, onCreated }: Props): JSX.Element {
    const dispatch = useDispatch();
    const [name, setName] = useState('');
    const [drawings, setDrawings] = useState<UploadFile[]>([]);
    const [photos, setPhotos] = useState<UploadFile[]>([]);
    const [status, setStatus] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const busy = status !== null;

    const create = async (): Promise<void> => {
        const input = {
            projectId, name, drawings: originals(drawings), photos: originals(photos),
        };
        const problem = validate(input);
        if (problem) {
            setError(problem);
            return;
        }
        setError(null);
        setStatus(t('Creating the task…'));
        try {
            const created = dispatch(createTaskAsync(taskPayload(input), setStatus)) as unknown as Promise<Task>;
            const task = await created;
            const reference = referencePayload(input, task.id);
            if (reference) {
                setStatus(t('Adding the photos…'));
                await (dispatch(createReferenceAsync(reference, setStatus)) as unknown as Promise<void>);
            }
            onCreated(task);
        } catch (e: unknown) {
            setStatus(null);
            setError(e instanceof Error ? e.message : String(e));
        }
    };

    return (
        <Modal
            visible
            title={t('New task')}
            okText={t('Create')}
            onOk={create}
            onCancel={onClose}
            okButtonProps={{ disabled: busy }}
            cancelButtonProps={{ disabled: busy }}
            closable={!busy}
            maskClosable={false}
            className='cvat-new-task-modal'
        >
            <Input
                placeholder={t('Task name')}
                value={name}
                disabled={busy}
                onChange={(event) => setName(event.target.value)}
                onPressEnter={create}
                aria-label={t('Task name')}
                autoFocus
            />
            <DropZone
                label={t('Drawings')}
                hint={t('A PDF or images of the floor plans')}
                files={drawings}
                accepts={isDrawing}
                disabled={busy}
                onChange={setDrawings}
            />
            <DropZone
                label={t('References (optional)')}
                hint={t('Photos of the rooms')}
                files={photos}
                accepts={isPhoto}
                disabled={busy}
                onChange={setPhotos}
            />
            {status && <Alert type='info' showIcon message={status} />}
            {error && <Alert type='error' showIcon message={error} />}
        </Modal>
    );
}
